import { useQuery } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { Link, useParams, useSearchParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { LayoutHeader } from "views/applications-id/components/LayoutHeader";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { useApplicationDecision } from "shared/hooks/use-application-decision";
import { useEffect, useMemo, useState } from "react";
import { ActivityLog } from "../../shared/app/ActivityLog";
import { DecisionConfirmationModal } from "views/applications-id/components/DecisionConfirmationModal";
import { Application } from "types/application";
import { Template } from "types/landlord/verificationTemplate";
import { Button, Show } from "shared/components";
import { ApplicationStatus } from "shared/types";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { ApplicantStatus, ApplicantType } from "shared/types/application";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { MoveModal } from "../../shared/app/MoveModal";
import { RefundModal } from "shared/app/RefundModal";
import { ApplicationActions } from "shared/utils/application-actions";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { UncancelModal } from "shared/app/UncancelModal";
import { SplitModal } from "shared/app/SplitModal";
import { LoadingSkeletonSlice } from "./slices/LoadingSkeletonSlice";
import { HeaderTabs } from "views/applications-id/components/LayoutHeader/types";
import { ApplicationTab } from "views/applications-id/slices/application-tab";
import { TaskTableView } from "shared/app/TaskTableView";
import {
  TaskSubjectPayload,
  TaskSubject
} from "shared/app/TaskTableView/types";
import { Notes } from "shared/app/Notes";
import { useNotesController } from "shared/queries/useNotesController";
import { track } from "shared/telemetry/track";
import { PaywalledPanel } from "shared/app/BoomCrmPaywall/PaywalledPanel";
import { useIsCrmFeatureGated } from "shared/app/BoomCrmPaywall/hooks";
import { FEATURES } from "types/landlord/partner";

export const ApplicationsIdOverview = () => {
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  // Tasks is Pro-only; the tab stays visible for the whole CRM ecosystem and
  // paywalls its content, like the /tasks page (CRM-759).
  const isTasksGated = useIsCrmFeatureGated(FEATURES.TASKS);
  const [searchParams] = useSearchParams();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showActivityLog, setShowActivityLog] = useState<boolean>(false);
  const { applicationId } = useParams();
  const [showDecisionFlowModal, setShowDecisionFlowModal] =
    useState<boolean>(false);
  const [showDecisionConfirmationModal, setDecisionConfirmationModal] =
    useState<boolean>(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);

  const [showNotesModal, setShowNotesModal] = useState<boolean>(
    Boolean(searchParams.get("note_id"))
  );
  const [hideApplicantNamesAndUnitInfo, setHideApplicantNamesAndUnitInfo] =
    useState(false);
  const [splitModalId, setSplitModalId] = useState<string | undefined>();

  const [previewTemplate, setPreviewTemplate] = useState<Template>();

  const [selectedTab, setSelectedTab] = useState<HeaderTabs>(
    HeaderTabs.APPLICATION
  );
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const applicationMeta = useQuery<Application>({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(applicationId || "")
  });

  const notesController = useNotesController(String(applicationId));

  useEffect(() => {
    if (!applicationId) return;
    track("application.page_view", {
      application_id: String(applicationId)
    });
  }, [applicationId]);

  // Warm summary + decision caches in parallel so the report page and other
  // consumers get instant navigation. The full applicationMeta above is still
  // used for the applicants table and modals until a follow-up refactor.
  useApplicationSummary(applicationId);
  useApplicationDecision(applicationId);

  const { type, permissions } = useAuthContext();

  const {
    unit,
    property,
    applicants,
    status,
    submitted_at,
    verification_template,
    pending_magic_links,
    requires_overrides_identity_verification,
    decision_at
  } = applicationMeta.data || {};

  const canMakeDecision = useMemo(() => {
    return !new ApplicationActions({
      status: status || ApplicationStatus.Started,
      permissions,
      applicants: applicants || [],
      requirements: applicationMeta.data?.make_decision_requirements
    }).makeDecision.disabled;
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions
  ]);

  const notCancelledApplicants = useMemo(() => {
    return applicants?.filter(
      (applicant) => applicant.application_status !== ApplicantStatus.Canceled
    );
  }, [applicants]);

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (applicationMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading application
          </h1>

          <Link to="/applications">
            <Button>Go back</Button>
          </Link>
        </div>
      </Layout>
    );

  // handle loading state here
  if (applicationMeta.isLoading) return <LoadingSkeletonSlice />;
  if (!unit || !property || !applicationMeta.data) return null;

  return (
    <Layout
      sectionProps={{
        // Hide applicants' names and unit info in header when scroll down
        onScroll: (e) => {
          setHideApplicantNamesAndUnitInfo(e.currentTarget.scrollTop > 0);
        }
      }}
      sectionClassName="!p-0"
      headerClassName="z-[2] bg-white border-b border-alpha-100"
      header={
        <LayoutHeader
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          application={applicationMeta.data}
          hideApplicantNamesAndUnitInfo={hideApplicantNamesAndUnitInfo}
          disableDecisionFlow={!canMakeDecision}
          applicants={notCancelledApplicants}
          invitees={pending_magic_links || []}
          applicationId={applicationId || ""}
          viewTemplate={() => {
            setPreviewTemplate(verification_template);
          }}
          requirements={applicationMeta.data.make_decision_requirements}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          applicationStatus={status || ApplicationStatus.Started}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          submittedAt={submitted_at || ""}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          onCancelClick={() => setShowCancelModal(true)}
          onUncancelClick={() => setShowUncancelModal(true)}
          onRefundClick={() => setShowRefundModal(true)}
          onMoveClick={() => setShowMoveModal(true)}
          onSplitClick={() => setSplitModalId(applicationId)}
          decisionAt={decision_at || ""}
          setShowNotesModal={setShowNotesModal}
          numberOfNotes={notesController.totalNotesCount}
        />
      }
    >
      <Show if={selectedTab === HeaderTabs.APPLICATION}>
        <ApplicationTab
          applicationId={applicationId || ""}
          applicationMeta={applicationMeta}
          canMakeDecision={canMakeDecision}
          notCancelledApplicants={notCancelledApplicants}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          onViewTemplate={() => {
            setPreviewTemplate(verification_template);
          }}
        />
      </Show>

      <Show if={selectedTab === HeaderTabs.TASKS && isTasksGated}>
        <div className="p-6">
          <PaywalledPanel feature="tasks" className="min-h-[600px]" />
        </div>
      </Show>

      <Show if={selectedTab === HeaderTabs.TASKS && !isTasksGated}>
        <div className="p-6">
          <TaskTableView
            showCompletedTasks={showCompletedTasks}
            onShowCompletedTasksChange={setShowCompletedTasks}
            hasHeaderTitle={false}
            relatedTo={{
              subject_type: TaskSubjectPayload.BOOM_SCREEN_APPLICATIONS,
              subject_id: applicationMeta.data.id
            }}
            defaultSubject={{
              id: applicationMeta.data.id,
              name: applicants?.[0]
                ? `${applicants[0].first_name} ${applicants[0].last_name}`.trim()
                : "",
              property,
              type: TaskSubject.BOOM_SCREEN_APPLICATIONS
            }}
          />
        </div>
      </Show>

      <TemplateReviewModal
        opened={!!previewTemplate}
        onClose={() => setPreviewTemplate(undefined)}
        template={previewTemplate}
        applicationId={applicationId}
        applicationStatus={status}
        propertyId={property.id}
      />
      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          propertyName={property.name}
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={applicationId || ""}
          applicants={applicationMeta.data?.applicants}
        />
      </Show>
      <Show if={showDecisionFlowModal}>
        <MakeDecisionModal
          opened={showDecisionFlowModal}
          onClose={() => setShowDecisionFlowModal(false)}
          applicationId={applicationId || ""}
        />
      </Show>

      <DecisionConfirmationModal
        opened={showDecisionConfirmationModal}
        onContinue={() => {
          setDecisionConfirmationModal(false);
          setShowDecisionFlowModal(true);
        }}
        onClose={() => setDecisionConfirmationModal(false)}
      />
      <Show if={showInviteApplicantsModal}>
        <InviteAndCreateModal
          disabledRoles={
            [...(applicants || []), ...(pending_magic_links || [])].some(
              (applicant) =>
                [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                  applicant.role as ApplicantType
                )
            )
              ? []
              : [ApplicantType.CoSigner]
          }
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          applicationId={Number(applicationId)}
          propertyId={property.id}
          propertyName={property.name}
          unitId={unit.id}
          unitName={unit.title}
          onInviteRefreshKey={["APPLICATION", applicationId]}
        />
      </Show>

      <Show if={showNotesModal}>
        <Notes
          showNotes={showNotesModal}
          setShowNotes={setShowNotesModal}
          application={applicationMeta.data}
          notesController={notesController}
        />
      </Show>
      <Show if={Boolean(splitModalId)}>
        <SplitModal
          isOpen={Boolean(splitModalId)}
          applicationId={splitModalId ?? ""}
          onClose={() => setSplitModalId(undefined)}
        />
      </Show>
      <CancelOrRefundModal
        applicants={applicants}
        applicationId={showCancelModal ? String(applicationId) : ""}
        onClose={() => setShowCancelModal(false)}
        onSuccess={() => {
          applicationMeta.refetch();
        }}
      />
      <UncancelModal
        applicationId={showUncancelModal ? String(applicationId) : ""}
        onClose={() => setShowUncancelModal(false)}
        onSuccess={() => {
          applicationMeta.refetch();
        }}
      />
      <RefundModal
        applicationId={showRefundModal ? String(applicationId) : ""}
        onClose={() => setShowRefundModal(false)}
        queryKey={["APPLICATION", applicationId]}
      />
      <MoveModal
        applicationId={showMoveModal ? String(applicationId) : ""}
        onClose={() => setShowMoveModal(false)}
      />
    </Layout>
  );
};
